import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import ModalComponent from './ModalComponent';

import InputComponent from '../FormComponents/InputComponent';
import SelectComponent, { SelectOption } from '../FormComponents/SelectComponent';

import { addVariantExternalCode, getVariantExternalCodes } from '../../api/products';
import { getRequestedLanguages } from '../../locale/languages';
import { ProductProperty, ProductVariant } from '../../utils/models/product';

interface Props {
    showModal: boolean;
    customerCode: string;
    variant: ProductVariant;
    properties: string[];
    onCancel: () => void;
    onSave: (selectedCode: string) => void;
}

const AssociateIdModalComponent: FC<Props> = ({ showModal, customerCode, variant, properties, onCancel, onSave }) => {
    const language = getRequestedLanguages(window.berardi_accountUserData.language);
    const [isLoading, setIsLoading] = useState(false);
    const [isOnConfirm, setIsOnConfirm] = useState(false);
    const [selectedId, setSelectedId] = useState(variant.external_code ?? null);
    const [externalCodes, setExternalCodes] = useState<string[]>([]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const codes = await getVariantExternalCodes(variant.sku, customerCode).then((result) => {
                return result.data.data.external_codes;
            });
            setExternalCodes(codes);
        } catch (error) {
            console.log(error);
        }
        setIsLoading(false);
    }, [variant]);

    const handleConfirm = useCallback(async () => {
        if (externalCodes.length > 0 && selectedId) {
            onSave(selectedId);
        } else {
            if (selectedId) {
                if (!isOnConfirm) {
                    setIsOnConfirm(true);
                } else {
                    setIsLoading(true);
                    try {
                        await addVariantExternalCode(variant.sku, customerCode, selectedId);
                        onSave(selectedId);
                    } catch (error) {
                        console.log(error);
                    }
                    setIsLoading(false);
                }
            }
        }
    }, [variant, customerCode, externalCodes, selectedId, isOnConfirm]);

    const handleOnCancel = useCallback(() => {
        if (isOnConfirm) {
            setIsOnConfirm(false);
        } else {
            onCancel();
        }
    }, [isOnConfirm]);

    const limitedProperties = useMemo(() => {
        if (properties.length > 4) {
            return properties.slice(0, 4);
        }
        return properties;
    }, [properties]);

    const limitedVariantProperties = useMemo(() => {
        if (variant.properties.length > 4) {
            return variant.properties.slice(0, 4);
        }
        return variant.properties;
    }, [variant]);

    const headRow = useMemo(() => {
        return (
            <tr className="br-table__headRow">
                <th className="br-table__headRowCell -flex2">{language.product.table.art_code}</th>
                {isOnConfirm ? (
                    <th className="br-table__headRowCell -flex2">{language.product.table.art_code_customer}</th>
                ) : null}
                {limitedProperties.map((property: string) => {
                    return <th className="br-table__headRowCell">{property}</th>;
                })}
            </tr>
        );
    }, [variant, isOnConfirm]);

    const codesOptions: SelectOption[] = useMemo(() => {
        return externalCodes.map((code) => {
            return {
                id: code,
                value: code,
                label: code,
            };
        });
    }, [externalCodes]);

    const selectedOption: SelectOption | null = useMemo(() => {
        if (selectedId) {
            return {
                id: selectedId,
                value: selectedId,
                label: selectedId,
            };
        } else {
            return null;
        }
    }, [selectedId]);

    useEffect(() => {
        if (showModal) {
            setSelectedId(null);
            setIsOnConfirm(false);
            fetchData();
        }
    }, [showModal]);

    return (
        <ModalComponent
            title={language.modals.associate_id.title}
            isLoading={isLoading}
            showModal={showModal}
            onClose={onCancel}
            onCancel={handleOnCancel}
            onConfirm={handleConfirm}
            confirmButtonText={
                !isOnConfirm && externalCodes.length > 0 ? language.modals.associate_id.associate_button : null
            }
            classes={['-associatedId']}
        >
            {isOnConfirm ? (
                <div className="textConfirm">
                    <p className="textConfirm-warning">{language.modals.associate_id.warning_text}</p>
                    <p className="textConfirm-explanation">{language.modals.associate_id.explanation_confirm}</p>
                </div>
            ) : null}
            <table className="br-table -productVariants">
                <thead className="br-table__content">{headRow}</thead>
                <tbody className="br-table__content">
                    <tr className="br-table__row" key={variant.sku}>
                        <td className="br-table__rowCell -flex2 -full-width -artCode">
                            <label>{language.product.table.art_code}</label>
                            <span>{variant.sku}</span>
                        </td>
                        {isOnConfirm ? (
                            <td className="br-table__rowCell -flex2 -full-width -artCodeCustomer">
                                <label>{language.product.table.art_code_customer}</label>
                                <span>{selectedId}</span>
                            </td>
                        ) : null}
                        {limitedVariantProperties.map((property: ProductProperty) => {
                            return (
                                <td className="br-table__rowCell">
                                    <label>{property.code}</label>
                                    {property.value}
                                </td>
                            );
                        })}
                    </tr>
                </tbody>
            </table>
            {!isOnConfirm ? <p>{language.modals.associate_id.paragraph}</p> : null}
            {!isOnConfirm ? (
                externalCodes.length > 0 ? (
                    <SelectComponent
                        label={language.modals.associate_id.customer_code}
                        options={codesOptions}
                        selectedValue={selectedOption}
                        onChange={(value) => {
                            if (value) {
                                setSelectedId(value.value as string);
                            }
                        }}
                    />
                ) : (
                    <InputComponent
                        label={language.modals.associate_id.customer_code}
                        placeholder={language.modals.associate_id.placeholder}
                        value={selectedId ?? ''}
                        onChange={(value) => {
                            setSelectedId(value);
                        }}
                    />
                )
            ) : null}
        </ModalComponent>
    );
};

export default AssociateIdModalComponent;
